import { NormalizedUnitValue } from '@/helpers/unit-formatter/shared/types';

export const BOOLEAN_LABEL_SETS = {
  boolean: { true: 'TRUE', false: 'FALSE' },
  string: { true: 'TRUE', false: 'FALSE' },
  'on-off': { true: 'ON', false: 'OFF' },
  'open-closed': { true: 'OPEN', false: 'CLOSED' },
} as const;

export type BooleanLabelTarget = keyof typeof BOOLEAN_LABEL_SETS;

const TRUE_LABELS = new Set([
  ...Object.values(BOOLEAN_LABEL_SETS).map((labels) => labels.true.toLowerCase()),
  '1',
]);
const FALSE_LABELS = new Set([
  ...Object.values(BOOLEAN_LABEL_SETS).map((labels) => labels.false.toLowerCase()),
  '0',
]);

export function resolveBooleanValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (TRUE_LABELS.has(normalized)) return true;
    if (FALSE_LABELS.has(normalized)) return false;
  }

  return Boolean(value);
}

/**
 * Whether a value is one this module can actually read, as opposed to one
 * `resolveBooleanValue` would fall through to JS truthiness on. Callers that must
 * not guess — a settings field that would otherwise stage a wrong value — check
 * this first and treat `false` as "no value".
 */
export function isBooleanLike(value: unknown): boolean {
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  if (typeof value !== 'string') return false;

  const normalized = value.trim().toLowerCase();
  return TRUE_LABELS.has(normalized) || FALSE_LABELS.has(normalized);
}

export function coerceBooleanLike(
  unitValue: NormalizedUnitValue,
  targetKey: string
): NormalizedUnitValue {
  const key = targetKey.toLowerCase() as BooleanLabelTarget;
  const labels = BOOLEAN_LABEL_SETS[key] ?? BOOLEAN_LABEL_SETS.boolean;
  const isTrue = resolveBooleanValue(unitValue.value);
  return { value: isTrue ? labels.true : labels.false, units: '' };
}
